import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Building2,
  CalendarClock,
  CreditCard,
  LogIn,
  RefreshCw,
  ReceiptText,
  ShieldCheck,
  ShoppingCart,
  TrendingUp,
  Users,
  WalletCards,
} from 'lucide-react';
import { API_URL } from '../config/api';
import { formatDateTime } from '../utils/dateFormat';
import { AUTH_SESSION_CHANGED_EVENT, clearAuthSession, pushImpersonationSession, setAuthSession } from '../utils/auth';
import { getBrandImageUrl, getProfileImageUrl } from '../utils/browserBranding';

const formatMoney = (value) => `Rs. ${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const formatRole = (role = '') => (role === 'whitelabel_admin' ? 'White Label Admin' : role === 'admin' ? 'Company Admin' : role.replace(/_/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase()));

const getCompanyAvatarUrl = (company) => {
  if (company?.logo_url && company.logo_url !== '/assets/BizTrack_Logo.png') {
    return getBrandImageUrl(company.logo_url);
  }
  if (company?.profile_pic) {
    return getProfileImageUrl(company.profile_pic);
  }
  if (company?.logo_url) {
    return getBrandImageUrl(company.logo_url);
  }
  return '';
};

export default function SuperAdminDashboard({ onSelectCompany }) {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [demoName, setDemoName] = useState('');
  const [demoBusy, setDemoBusy] = useState(false);
  const [demoAccount, setDemoAccount] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');

  const getHeaders = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/users/superadmin/companies`, { headers: getHeaders() });
      setCompanies(res.data);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

  const toAbsolutePortalUrl = (value) => {
    const url = String(value || '').trim();
    if (!url) return '';
    if (/^https?:\/\//i.test(url)) return url;
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    if (!origin) return url;
    return `${origin}${url.startsWith('/') ? '' : '/'}${url}`;
  };

  const createDemoAdmin = async () => {
    if (demoBusy) return;
    const nextDemoName = demoName.trim();
    if (!nextDemoName) {
      alert('Demo admin name is required');
      return;
    }
    if (!window.confirm(`Create demo admin "${nextDemoName}" active for exactly 5 days?`)) return;

    setDemoBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/users/demo-admin`, { name: nextDemoName }, { headers: getHeaders() });
      const createdDemo = {
        ...res.data,
        assigned_portal_url: toAbsolutePortalUrl(res.data?.assigned_portal_url || '/')
      };
      setDemoAccount(createdDemo);
      setDemoName('');
      await fetchCompanies();
      alert(`Demo Admin Created\nName: ${createdDemo.name}\nID: ${createdDemo.username}\nPassword: ${createdDemo.password}\nPortal: ${createdDemo.assigned_portal_url}\nValid Till: ${formatDateTime(createdDemo.expires_at)}`);
    } catch (err) {
      alert(err.response?.data?.message || 'Demo admin create failed');
    } finally {
      setDemoBusy(false);
    }
  };

  const filtered = useMemo(() => {
    let list = companies;

    if (activeFilter === 'active') {
      list = list.filter((company) => company.is_active);
    } else if (activeFilter === 'renewals') {
      list = list.filter((company) => Number(company.renewals_due || 0) > 0);
    } else if (activeFilter === 'revenue') {
      list = list.filter((company) => Number(company.total_revenue || 0) > 0);
    } else if (activeFilter === 'customers') {
      list = list.filter((company) => Number(company.total_customers || 0) > 0);
    } else if (activeFilter === 'sales') {
      list = list.filter((company) => Number(company.total_sales || 0) > 0);
    } else if (activeFilter === 'deviceSales') {
      list = list.filter((company) => Number(company.device_sales || 0) > 0);
    } else if (activeFilter === 'serviceSales') {
      list = list.filter((company) => Number(company.service_sales || 0) > 0);
    }

    const term = search.trim().toLowerCase();
    if (!term) return list;
    return list.filter((company) => (
      String(company.company_name || '').toLowerCase().includes(term) ||
      formatRole(company.company_role || 'admin').toLowerCase().includes(term)
    ));
  }, [companies, search, activeFilter]);

  const totals = useMemo(() => ({
    revenue: companies.reduce((sum, company) => sum + parseFloat(company.total_revenue || 0), 0),
    customers: companies.reduce((sum, company) => sum + parseInt(company.total_customers || 0, 10), 0),
    sales: companies.reduce((sum, company) => sum + parseInt(company.total_sales || 0, 10), 0),
    deviceSales: companies.reduce((sum, company) => sum + parseInt(company.device_sales || 0, 10), 0),
    serviceSales: companies.reduce((sum, company) => sum + parseInt(company.service_sales || 0, 10), 0),
    renewals: companies.reduce((sum, company) => sum + parseInt(company.renewals_due || 0, 10), 0),
    pending: companies.reduce((sum, company) => sum + parseFloat(company.pending_amount || 0), 0),
    active: companies.filter((company) => company.is_active).length,
  }), [companies]);

  const summaryCards = [
    { label: 'Accounts', value: companies.length, icon: Building2, color: '#0b66ae', filterKey: 'all' },
    { label: 'Active Accounts', value: totals.active, icon: ShieldCheck, color: '#8cc63f', filterKey: 'active' },
    { label: 'Total Revenue', value: formatMoney(totals.revenue), icon: TrendingUp, color: '#10b981', filterKey: 'revenue' },
    { label: 'Total Customers', value: totals.customers, icon: Users, color: '#2563eb', filterKey: 'customers' },
    { label: 'Total Sales', value: totals.sales, icon: ShoppingCart, color: '#7c3aed', filterKey: 'sales' },
    { label: 'Device Sales', value: totals.deviceSales, icon: ShoppingCart, color: '#0b66ae', filterKey: 'deviceSales' },
    { label: 'Service Sales', value: totals.serviceSales, icon: ReceiptText, color: '#f59e0b', filterKey: 'serviceSales' },
    { label: 'Renewals Due', value: totals.renewals, icon: CalendarClock, color: '#dc2626', filterKey: 'renewals' },
  ];

  const getCompanyStats = (company) => [
    { label: 'Employees', value: company.total_employees, icon: Users, color: '#0b66ae' },
    { label: 'Dealers', value: company.total_dealers, icon: Building2, color: '#0891b2' },
    { label: 'Customers', value: company.total_customers, icon: Users, color: '#2563eb' },
    { label: 'Device Sales', value: company.device_sales || 0, icon: ShoppingCart, color: '#0b66ae' },
    { label: 'Service Sales', value: company.service_sales || 0, icon: ReceiptText, color: '#f59e0b' },
    { label: "Today's Sales", value: company.today_sales, icon: Activity, color: '#d97706' },
    { label: 'Revenue', value: formatMoney(company.total_revenue), icon: WalletCards, color: '#10b981' },
    { label: 'Pending', value: formatMoney(company.pending_amount), icon: CreditCard, color: '#ef4444' },
    { label: 'Renewals', value: company.renewals_due, icon: RefreshCw, color: '#dc2626' },
  ];

  const enterCompanyAccount = async (company) => {
    if (!company?.admin_id) return;
    if (!company.is_active) {
      alert('Inactive account cannot be entered');
      return;
    }
    if (!window.confirm(`Enter ${company.company_name || 'this account'}?`)) return;

    try {
      const currentToken = localStorage.getItem('token');
      const currentUser = localStorage.getItem('user');
      const res = await axios.post(`${API_URL}/api/auth/impersonate/${company.admin_id}`, {}, { headers: getHeaders() });

      pushImpersonationSession({ token: currentToken, user: currentUser });
      const switchedUser = setAuthSession({ token: res.data.token, user: res.data.user });
      window.dispatchEvent(new CustomEvent(AUTH_SESSION_CHANGED_EVENT, { detail: { user: switchedUser } }));
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const message = err.response?.data?.message || 'Could not enter this account';
      if (err.response?.status === 401 || message === 'Invalid or expired token') {
        clearAuthSession();
        navigate('/login');
        return;
      }
      alert(message);
    }
  };

  return (
    <div className="company-overview fixed-panel-page tight">
      <div className="fixed-panel-header">
      <div className="company-overview-header">
        <div>
          <span className="company-kicker">Superadmin workspace</span>
          <h2>Account Overview</h2>
          {/* <p>All CRM accounts, performance signals and renewal health in one clean command dashboard.</p> */}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap' }}>
          <input
            value={demoName}
            onChange={(event) => setDemoName(event.target.value)}
            placeholder="Demo admin name"
            maxLength={120}
            disabled={demoBusy}
            style={{ width: '220px', maxWidth: '100%', minHeight: '42px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 12px', background: 'white', color: '#0f172a', fontWeight: 700 }}
          />
          <button
            type="button"
            onClick={createDemoAdmin}
            disabled={demoBusy}
            style={{ minHeight: '42px', borderRadius: '8px', background: demoBusy ? '#94a3b8' : '#f59e0b', color: 'white', padding: '0 16px', boxShadow: '0 8px 18px rgba(245,158,11,0.24)', whiteSpace: 'nowrap' }}
          >
            {demoBusy ? 'Creating Demo...' : 'Create 5 Day Demo Admin'}
          </button>
          <button type="button" onClick={fetchCompanies}>
            <RefreshCw size={16} /> Refresh
          </button>
        </div>
      </div>

      {demoAccount && (
        <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '8px', padding: '10px 12px', marginBottom: '14px', display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <strong style={{ color: '#92400e', fontSize: '13px' }}>Demo Admin Active till {formatDateTime(demoAccount.expires_at)}</strong>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            <code style={{ background: 'white', border: '1px solid #fbbf24', borderRadius: '6px', padding: '6px 8px', color: '#92400e', fontSize: '12px' }}>ID: {demoAccount.username}</code>
            <code style={{ background: 'white', border: '1px solid #fbbf24', borderRadius: '6px', padding: '6px 8px', color: '#92400e', fontSize: '12px' }}>Password: {demoAccount.password}</code>
            <a href={demoAccount.assigned_portal_url} target="_blank" rel="noreferrer" style={{ background: 'white', border: '1px solid #fbbf24', borderRadius: '6px', padding: '6px 8px', color: '#92400e', fontSize: '12px', fontFamily: 'monospace', textDecoration: 'none' }}>Portal</a>
          </div>
        </div>
      )}

      <div className="company-summary-grid">
        {summaryCards.map(({ label, value, icon: Icon, color, filterKey }) => (
          <div
            className={`company-summary-card clickable ${activeFilter === filterKey ? 'active' : ''}`}
            style={{ '--accent': color }}
            key={label}
            onClick={() => setActiveFilter((current) => (current === filterKey ? 'all' : filterKey))}
            title={`Filter by ${label}`}
          >
            <div>
              <Icon size={20} />
            </div>
            <span>{label}</span>
            <strong>{value}</strong>
          </div>
        ))}
      </div>

      {/*
      <div className="company-search-box">
        <Search size={18} />
        <input
          type="text"
          placeholder="Search account or role..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>
      */}
      </div>

      <div className="fixed-panel-body">
      {loading ? (
        <div className="company-empty">Loading account dashboard...</div>
      ) : filtered.length === 0 ? (
        <div className="company-empty">No accounts found</div>
      ) : (
        <div className="company-card-grid">
          {filtered.map((company) => (
            <article className={`company-card ${company.is_active ? '' : 'inactive'}`} key={company.admin_id}>
              <div className="company-card-header">
                <div className="company-avatar">
                  {getCompanyAvatarUrl(company) ? (
                    <img src={getCompanyAvatarUrl(company)} alt={company.company_name} />
                  ) : (
                    company.company_name?.charAt(0)?.toUpperCase() || 'C'
                  )}
                </div>
                <div className="company-title-block">
                  <h3>{company.company_name}</h3>
                  <div>
                    <span className="role-pill">{formatRole(company.company_role || 'admin')}</span>
                    <span className={`status-pill ${company.is_active ? 'active' : 'inactive'}`}>
                      {company.is_active ? 'Active account' : 'Inactive account'}
                    </span>
                  </div>
                </div>
                <div className="company-card-actions">
                  <button type="button" className="company-enter-button" onClick={() => enterCompanyAccount(company)} disabled={!company.is_active}>
                    <LogIn size={16} /> Enter Account
                  </button>
                  <button type="button" onClick={() => onSelectCompany(company)}>
                    View Details <ArrowRight size={16} />
                  </button>
                </div>
              </div>

              <div className="company-metric-grid">
                {getCompanyStats(company).map(({ label, value, icon: Icon, color }) => (
                  <div className="company-metric" style={{ '--accent': color }} key={label}>
                    <Icon size={16} />
                    <strong>{value}</strong>
                    <span>{label}</span>
                  </div>
                ))}
              </div>

              {Number(company.renewals_due || 0) > 0 && (
                <div className="company-renewal-alert">
                  <AlertTriangle size={16} />
                  <span>{company.renewals_due} renewals due within 30 days</span>
                </div>
              )}
            </article>
          ))}
        </div>
      )}
      </div>
    </div>
  );
}
